import { useState, useRef, useEffect, useContext } from 'react';
import NewSideBar from '../../components/Navigation/NewSideBar';
import { useNavigate } from 'react-router-dom';
import { useTextField } from '@react-aria/textfield';
import { LayoutContext } from '../../utils/contexts/LayoutContext';

import { PencilIcon } from '@heroicons/react/24/outline';

import useInterview from '../../utils/hooks/useInterview';
import { useDebounce } from '../../utils/hooks/useDebounce';
import FullTable from '../../components/Table/FullTable';

const QuestionBank = () => {
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { fetchQuestionsBank, deleteOneQuestion } = useInterview();
  const [questions, setQuestions] = useState([]);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const debouncedSearch = useDebounce(search, 300);

  const { setPadding } = useContext(LayoutContext);
  useEffect(() => {
    setPadding('p-0');
    return () => setPadding('p-4');
  }, [setPadding]);

  const fetchData = async () => {
    setIsLoading(true);
    const result = await fetchQuestionsBank(debouncedSearch, page, pageSize);
    setIsLoading(false);

    if (result.success) {
      const mapped = result.data.results.map(({ uuid, name, body }) => ({
        id: uuid,
        title: name,
        question: body,
      }));
      setQuestions(mapped);
      setItems(mapped);
      setTotalCount(result.data.count);
      const totalPages = Math.ceil(result.data.count / pageSize);
      setPageCount(totalPages);
    } else {
      console.error('Failed to load question bank');
    }
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearch, page, pageSize]);

  useEffect(() => {
    setPage(1); // reset page when search changes
  }, [search]);

  const navigate = useNavigate();

  const visibleItems = items;

  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [mode, setMode] = useState('view');
  const [activeId, setActiveId] = useState(null);

  const inputRef = useRef();
  useEffect(() => {
    setItems(questions);
  }, [questions]);

  const { labelProps, inputProps } = useTextField(
    {
      inputElementType: 'input',
      value: search,
      onChange: (e) => setSearch(e),
      placeholder: 'Search Questions',
    },
    inputRef,
  );

  const isEditMode = mode === 'edit';

  const toggleSelection = (id) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleDelete = async () => {
    const idsToDelete = Array.from(selectedKeys);
    const results = await Promise.all(
      idsToDelete.map((uuid) => deleteOneQuestion(uuid)),
    );

    results.forEach((r, i) => {
      if (!r.success) {
        alert(`Failed to delete ${idsToDelete[i]}: ${r.message}`);
      }
    });

    fetchData();
  };

  // inside QuestionBank component, before the return…

  // 2.1) Shape the data
  const fullTableData = visibleItems.map((q, idx) => ({
    uuid: q.id,
    title: q.title,
    question: q.question,
    no: (page - 1) * pageSize + idx + 1, // <-- add this
  }));

  // 2.2) Define columns
  const columns = [
    {
      key: 'sequence',
      label: 'No.',
      className: 'text-center w-10',
      render: (_, idx) => (page - 1) * pageSize + idx + 1,
    },
    {
      key: 'title',
      label: 'Title',
      className: 'text-left',
      render: (item) => item.title,
    },
    {
      key: 'question',
      label: 'Question',
      className: 'text-left',
      render: (item) => item.question,
    },
    {
      key: 'skills',
      label: 'Skills',
      className: 'text-left',
      render: (item) => item.skills?.map((skill) => skill.name).join(', '),
    },
    {
      key: 'actions',
      label: '',
      className: 'text-center w-8',
      render: (item) => (
        <PencilIcon
          className="h-5 w-5 text-gray-500 cursor-pointer hover:text-blue-500"
          onClick={() =>
            navigate(`/question/edit-question/${item.uuid}`, {
              state: { no: item.no },
            })
          }
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col h-screen bg-white text-gray-900 dark:text-white transition-colors duration-300">
      <div className="flex justify-center dark:bg-gray-900 h-full">
        <NewSideBar />
        <div className="p-6 w-full overflow-y-scroll">
          <h2 className="mt-6 text-xl font-bold text-gray-900 dark:text-white">
            Question Bank
          </h2>

          <div className="flex gap-4 my-2">
            <div className="flex flex-col flex-1">
              <label {...labelProps} className="sr-only">
                Search Questions
              </label>
              <input
                {...inputProps}
                ref={inputRef}
                className="p-2 rounded flex-1 dark:bg-gray-700 dark:text-white dark:border-gray-600 border-2 border-gray-200"
              />
            </div>
            <button
              className="p-2 rounded ml-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm"
              onClick={() =>
                navigate('/question/create', {
                  state: { questionCount: visibleItems.length },
                })
              }
            >
              + Create Question
            </button>
          </div>

          <div className="flex items-center gap-4 border-b border-gray-200 pb-2 mb-2">
            <span className="font-semibold text-sm">
              Showing {visibleItems.length} of {totalCount}
            </span>
            <button
              onClick={() => setMode(isEditMode ? 'view' : 'edit')}
              className="text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              {isEditMode ? 'Done' : 'Edit'}
            </button>
            {isEditMode && selectedKeys.size > 0 && (
              <button
                onClick={handleDelete}
                className="text-sm text-red-600 hover:underline"
              >
                Delete Question from Question Bank
              </button>
            )}
          </div>
          <FullTable
            data={fullTableData}
            columns={columns}
            showOrderNumber={false} // show sequence number
            loading={isLoading}
            enableSearch={false} // you already have your own search box above
            enableCheckbox={isEditMode} // show checkboxes only in Edit mode
            enableDnd={false} // no drag-and-drop here
            emptyMessage="No questions found"
            onSelectionChange={(uuids) => {
              setSelectedKeys(new Set(uuids));
            }}
          />

          <div className="flex justify-between items-center mt-4">
            <span className="text-gray-700 dark:text-gray-300">
              Page {page} of {pageCount}
            </span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(page - 1)}>
                &#9664;
              </button>
              {[...Array(pageCount)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i + 1)}
                  className={`px-3 py-1 border rounded ${
                    page === i + 1 ? 'bg-blue-500 text-white' : ''
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                disabled={page === pageCount}
                onClick={() => setPage(page + 1)}
              >
                &#9654;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionBank;
